// Cache-first "page image" layer for the Home and Health pages: every data GET made on
// those pages is stored in localStorage (24h, shared across tabs) and served from there
// with NO network round-trip until the leader hits Resync, a related write happens, or 24h passes.
import axios from 'axios';
import secureStorage from './secureStorage';

const PREFIX = 'hc:';
const TTL_MS = 24 * 60 * 60 * 1000;
// Pages whose data calls are cached (exact '/' for Home, prefixes for the health surfaces).
const PAGES = ['/lei-health', '/people-engine/health', '/maturity-health', '/roe-health', '/performance-health', '/roi-health', '/story-guide', '/lei-dashboard'];
// Endpoints cached wherever they are called from (server-cached health data).
const CACHED = ['/story-guide', '/transfer-health/', '/performance-health', '/roe/actuals/', '/health/quarter-deltas', '/health/snapshot-series', '/scenarios/roi-health', '/lei-health/', '/people-engine/health', '/people-engine/impact-rollup', '/lei-health/value-at-stake', '/people-engine/cohorts', '/segments/cohorts', '/maturity-assessment/'];
// Never cached: auth, live inboxes, cache status itself, binary exports, Mirror-personal data, UI copy.
const NEVER = ['/auth', '/notifications', '/health-cache', '/pdf', '/export', '/download', '/mirror', '/personal', '/mc/messaging', '/build-info', '/release-notes', '/sessions', '/messaging', '/telemetry', '/analytics/page-view'];
// Writes that never change health numbers (mirrors the server's cache-bump skip list) must not drop copies.
const BENIGN_WRITES = ['/auth', '/notifications', '/telemetry', '/health-cache/status', '/health-cache/warmup-report', '/analytics', '/comparison-analytics', '/quarterly-deck/pins', '/health/snapshots', '/modelling-settings/chain-transfer-plan/evaluate', '/user-preferences', '/notification-preferences', '/messaging', '/mirror/presence', '/mirror/frontline', '/personal', '/sessions', '/glossary', '/help', '/feedback', '/ai', '/chat', '/mc/'];
// Interactive writes that only touch their own view (checked before BENIGN/FULL).
const NARROW = [['/story-guide/', ['/story-guide']], ['/maturity-assessment/roadmap', ['/maturity-assessment/roadmap', '/story-guide']]];
// Platform-level writes change every page → wipe everything.
const FULL_CLEAR = ['/import', '/data-management', '/admin', '/seed', '/rbac', '/organizations', '/modelling-settings', '/health-cache/resync', '/lei-measures', '/kpi-library', '/benchmark', '/component-data-import', '/lei-raw', '/onboarding'];
// Aggregators read every domain → cleared on any non-benign write.
const AGGREGATORS = ['/story-guide', '/health/', '/home/', '/lei-dashboard'];

const onCachedPage = () => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/' || PAGES.some((x) => p === x || p.startsWith(x + '/'));
};

export const isCachedUrl = (url = '') => {
  if (NEVER.some((p) => url.startsWith(p)) || /[?&]refresh=1/.test(url)) return false;
  return CACHED.some((p) => url.startsWith(p)) || onCachedPage();
};

const store = () => window.localStorage;

function readEntry(key) {
  try {
    const raw = store().getItem(PREFIX + key);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (Date.now() - entry.at > TTL_MS) { store().removeItem(PREFIX + key); return null; }
    return entry;
  } catch { return null; }
}

export function readCached(key) { return readEntry(key)?.data ?? null; }

// Cache-first wrapper for raw fetch() callers (lib/authedFetch.js) — same keys/rules as the axios adapter.
export async function cachedFetch(doFetch, input, init = {}) {
  const url = typeof input === 'string' ? input : input?.url || '';
  const rel = url.replace(/^https?:\/\/[^/]+/, '').replace(/^\/api(?=\/)/, '');
  const method = (init.method || 'GET').toUpperCase();
  if (method !== 'GET' || !rel.startsWith('/') || !isCachedUrl(rel)) {
    const res = await doFetch();
    if (method !== 'GET' && res.ok) invalidateForWrite(rel);
    return res;
  }
  const viewAs = init.headers instanceof Headers ? init.headers.get('X-View-As-Org') || '' : '';
  const key = cacheKey(rel, viewAs);
  const entry = readEntry(key);
  if (entry) { stats.hits += 1; return new Response(JSON.stringify(entry.data), { status: 200, headers: { 'Content-Type': 'application/json', 'x-page-cache': 'hit' } }); }
  stats.inflight += 1; stats.lastActivity = Date.now();
  let res;
  try { res = await doFetch(); } finally { stats.inflight -= 1; stats.lastActivity = Date.now(); }
  if (res.ok && res.status === 200 && (res.headers.get('content-type') || '').includes('json')) {
    try { const data = await res.clone().json(); if (data && typeof data === 'object') { writeCached(key, data); stats.writes += 1; } } catch { /* not cacheable */ }
  }
  return res;
}

const allKeys = () => { try { return Object.keys(store()).filter((k) => k.startsWith(PREFIX)); } catch { return []; } };

function evictOldest() {
  const keys = allKeys();
  if (!keys.length) return false;
  let oldest = null; let oldestAt = Infinity;
  keys.forEach((k) => { try { const at = JSON.parse(store().getItem(k)).at || 0; if (at < oldestAt) { oldestAt = at; oldest = k; } } catch { store().removeItem(k); } });
  if (oldest) store().removeItem(oldest);
  return true;
}

export function writeCached(key, data) {
  const raw = JSON.stringify({ at: Date.now(), data });
  for (let i = 0; i < 25; i += 1) {
    try { store().setItem(PREFIX + key, raw); return; } catch { if (!evictOldest()) return; }
  }
}

export function clearCached(prefix = '') {
  try { allKeys().filter((k) => k.startsWith(PREFIX + prefix)).forEach((k) => store().removeItem(k)); } catch { /* noop */ }
  if (!prefix) setDemoReady(null);
}

// "Demo ready" marker written by Prepare demo; dropped whenever the copies are wiped or expire.
const READY_KEY = 'lei_demo_ready';
export function setDemoReady(info) {
  try { if (info) store().setItem(READY_KEY, JSON.stringify(info)); else store().removeItem(READY_KEY); } catch { /* noop */ }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('demo-ready:change'));
}
export function getDemoReady() {
  try {
    const raw = store().getItem(READY_KEY);
    if (!raw) return null;
    const info = JSON.parse(raw);
    if (Date.now() - info.at > TTL_MS) { store().removeItem(READY_KEY); return null; }
    return info;
  } catch { return null; }
}

// key = `${scope}|${viewAs}|${url}` → the url is everything after the second '|'.
const urlOfKey = (k) => k.split('|').slice(2).join('|');

export function clearCachedWhere(pred) {
  try { allKeys().filter((k) => pred(urlOfKey(k))).forEach((k) => store().removeItem(k)); } catch { /* noop */ }
}

// Only the pages a write can affect are dropped: its own resource family + the aggregators.
export function invalidateForWrite(url = '') {
  const narrow = NARROW.find(([p]) => url.startsWith(p));
  if (narrow) { clearCachedWhere((u) => narrow[1].some((p) => u.startsWith(p))); return; }
  if (BENIGN_WRITES.some((p) => url.startsWith(p))) return;
  if (FULL_CLEAR.some((p) => url.startsWith(p))) { clearCached(); return; }
  const seg = '/' + (url.split('?')[0].split('/').filter(Boolean)[0] || '');
  clearCachedWhere((u) => u.startsWith(seg) || AGGREGATORS.some((a) => u.startsWith(a)));
}

// Cache copies belong to a user (persisted scope set by AuthContext), not a session token.
export const cacheKey = (url, viewAs = '', params = '') => {
  const uid = secureStorage.getItem('cache_scope') || String(secureStorage.getItem('session_token') || '').slice(-12);
  return `${uid}|${viewAs || ''}|${url}${params}`;
};
const scope = (config) => {
  const viewAs = config.headers?.['X-View-As-Org'] || config.headers?.get?.('X-View-As-Org') || '';
  const params = config.params ? JSON.stringify(config.params) : '';
  return cacheKey(config.url, viewAs, params);
};

const synth = (config, data) => ({ data, status: 200, statusText: 'OK (page cache)', headers: { 'x-page-cache': 'hit' }, config, request: null });

// Live counters read by the demo warm-up frame poller (lib/demoWarmup.js).
export const stats = { inflight: 0, lastActivity: Date.now(), writes: 0, hits: 0 };
if (typeof window !== 'undefined') window.__pageCacheStats = stats;
export const isWarmupFrame = () => typeof window !== 'undefined' && window.self !== window.top && window.name === 'seertech-warmup';

// Wraps the axios adapter: cacheable GETs are served from the local copy without a request;
// misses go to the network and are stored; writes drop only the copies they can affect.
export function installPageCache(api) {
  const base = axios.getAdapter(api.defaults.adapter);
  api.defaults.adapter = async (config) => {
    const url = config.url || '';
    if ((config.method || 'get').toLowerCase() !== 'get' || !isCachedUrl(url) || (config.responseType && config.responseType !== 'json')) return base(config);
    const key = scope(config);
    const entry = readEntry(key);
    if (entry) { stats.hits += 1; return synth(config, entry.data); }
    stats.inflight += 1; stats.lastActivity = Date.now();
    let res;
    try { res = await base(config); } finally { stats.inflight -= 1; stats.lastActivity = Date.now(); }
    // The adapter sees the raw body (transformResponse runs later) — parse it so the copy is an object.
    let data = res.data;
    if (typeof data === 'string') { try { data = JSON.parse(data); } catch { data = null; } }
    if (res.status === 200 && data && typeof data === 'object') { writeCached(key, data); stats.writes += 1; }
    return res;
  };
  api.interceptors.response.use((res) => {
    const m = (res.config?.method || 'get').toLowerCase();
    if (m !== 'get' && res.status >= 200 && res.status < 300) invalidateForWrite(res.config?.url || '');
    return res;
  });
}
